<template>
  <v-card outlined class="mb-10 mx-2">
    <v-card-title :style="partnerColor.gradientStyle" class="title pt-4">
      <v-text-field
        dense
        v-model="search"
        append-icon="mdi-magnify"
        :label="$t('components.analytics.AnalyticsDataTableDialog.search')"
        single-line
        hide-details
        solo
      ></v-text-field>
      <v-spacer />
      <thg-charging-station-meter-reading-form-dialog ref="meterReadingFormDialog" :overflowBtn="true" />
    </v-card-title>
    <v-divider />
    <v-data-table
      :headers="meterReadingHeaders"
      :items="meterReadings"
      :search="search"
      :items-per-page="10"
      sort-by="timestamp.created"
      :sort-desc="true"
    >
      <template v-slot:[`item.status`]="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-chip dark :color="thgStatus(item.status).color">
              <v-icon v-on="on" v-bind="attrs">
                {{ thgStatus(item.status).icon }}
              </v-icon>
            </v-chip>
          </template>
          <span> {{ $t(thgStatus(item.status).text) }}</span>
        </v-tooltip>
      </template>
      <template v-slot:[`item.timestamp.created`]="{ item }">
        <span>{{ new Date(item.timestamp.created).toLocaleString("de-DE", dateOptions) }}</span>
      </template>
      <template v-slot:[`item.meterReading.startDate`]="{ item }">
        <span>{{ new Date(item.meterReading.startDate).toLocaleString("de-DE", dateOptions) }}</span>
      </template>
      <template v-slot:[`item.meterReading.endDate`]="{ item }">
        <span>{{ new Date(item.meterReading.endDate).toLocaleString("de-DE", dateOptions) }}</span>
      </template>

      <template slot="no-data">
        <latest-entries-card-empty class="my-6" />
        <v-container class="justify-center align-center">
          <v-card-text>
            {{ $t("components.thg.ThgChargingStationMeterReadingTable.emptyDataText") }}
          </v-card-text>
          <v-btn class="mb-2" dense small depressed :color="partnerColor.primary" @click="openDialog" dark>
            {{ $t("components.thg.ThgChargingStationMeterReadingTable.emptyDataBtnText") }}
          </v-btn>
        </v-container>
      </template>
      <template v-slot:[`footer.prepend`]>
        <thg-vehicle-table-legend-dialog />
      </template>
    </v-data-table>
  </v-card>
</template>

<script lang="ts">
import ThgChargingStationMeterReadingFormDialog from "@/components/thg/ThgChargingStationMeterReadingFormDialog.vue";
import ThgVehicleTableLegendDialog from "@/components/thg/ThgVehicleTableLegendDialog.vue";
import { IThgStatus } from "@/lib/interfaces/thg/thg-status.interface";
import { PartnerColor } from "@/lib/partnerColor";
import { thgStatusMap } from "@/lib/utility/thgStatusMap";
import { ThgChargingStationViewModelGen, ThgThgMeterReadingViewModelGen } from "@/services/thg/v1/data-contracts";
import { Component, Prop, Vue } from "vue-property-decorator";
import LatestEntriesCardEmpty from "../cards/LatestEntriesCardEmpty.vue";
import Debug from "../utility/Debug.vue";
import ThgQuoteExportDialog from "./ThgQuoteExportDialog.vue";

@Component({
  components: {
    Debug,
    ThgQuoteExportDialog,
    LatestEntriesCardEmpty,
    ThgVehicleTableLegendDialog,
    ThgChargingStationMeterReadingFormDialog
  }
})
export default class extends Vue {
  @Prop()
  selection!: ThgChargingStationViewModelGen;

  @Prop()
  meterReadings!: ThgThgMeterReadingViewModelGen[];

  search = "";

  partnerColor = new PartnerColor();
  thgStatusMap = thgStatusMap;

  get formDialog(): Vue & { openDialog: () => void } {
    return this.$refs.meterReadingFormDialog as Vue & { openDialog: () => void };
  }

  get dateOptions() {
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric"
    };
    return options;
  }

  openDialog() {
    this.formDialog.openDialog();
  }

  get meterReadingHeaders() {
    return [
      { text: "", value: null },
      { text: this.$t("components.thg.ThgChargingStationMeterReadingTable.status"), value: "status" },
      {
        text: this.$t("components.thg.ThgChargingStationMeterReadingTable.created"),
        value: "timestamp.created"
      },
      {
        text: this.$t("components.thg.ThgChargingStationMeterReadingTable.startDate"),
        value: "meterReading.startDate"
      },
      {
        text: this.$t("components.thg.ThgChargingStationMeterReadingTable.endDate"),
        value: "meterReading.endDate"
      },
      {
        text: this.$t("components.thg.ThgChargingStationMeterReadingTable.amountInKwh"),
        value: "meterReading.amountInKwh"
      }
    ];
  }

  addMeterReading() {
    this.$router.push({ path: `/charging-station/${this.selection.id}/meter-reading` });
  }

  thgStatus(thgStatusEnum: any): IThgStatus {
    return (
      this.thgStatusMap.get(thgStatusEnum) || {
        text: "components.thg.ThgStatusMap.UNDEFINED.text",
        textAlt: "components.thg.ThgStatusMap.UNDEFINED.textAlt",
        icon: "mdi-help",
        portalText: "components.thg.ThgStatusMap.UNDEFINED.portalText",
        color: "error"
      }
    );
  }
}
</script>
